// =====================================================================
// SupplyTypeQuickCreate.jsx - Quick create SupplyType (M-001 Faza 2)
// =====================================================================
// Sub-modal lansat din PrelevementFormModal cand ADMIN apasa
// "+ Nouveau type de fourniture". Necesita perm supply_draws.manage_catalog.all.
// =====================================================================

import { useState } from 'react';
import { supplyTypes as typesApi } from '../../services/api.js';

const CATEGORIES = [
  { value: 'filmage',   label: 'Filmage' },
  { value: 'carton',    label: 'Carton' },
  { value: 'etiquette', label: 'Étiquette' },
  { value: 'adhesif',   label: 'Adhésif' },
  { value: 'papier',    label: 'Papier' },
  { value: 'pochette',  label: 'Pochette' },
  { value: 'autre',     label: 'Autre' },
];

const UNITS = [
  { value: 'piece',    label: 'pièce' },
  { value: 'rouleau',  label: 'rouleau' },
  { value: 'metre',    label: 'mètre' },
  { value: 'feuille',  label: 'feuille' },
  { value: 'kg',       label: 'kg' },
];

export default function SupplyTypeQuickCreate({ onClose, onCreated }) {
  const [code, setCode]         = useState('');
  const [label, setLabel]       = useState('');
  const [category, setCategory] = useState('carton');
  const [unit, setUnit]         = useState('piece');
  const [notes, setNotes]       = useState('');
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const codeClean = code.trim().toUpperCase().replace(/\s+/g, '_');
    if (!codeClean) { setError('Le code est obligatoire.'); return; }
    if (!/^[A-Z0-9_]+$/.test(codeClean)) {
      setError('Le code ne peut contenir que des lettres majuscules, chiffres et underscore.');
      return;
    }
    if (!label.trim()) { setError('Le libellé est obligatoire.'); return; }

    setSaving(true);
    try {
      const created = await typesApi.create({
        code: codeClean,
        label: label.trim(),
        category,
        unit,
        notes: notes.trim() || undefined,
      });
      onCreated(created);
    } catch (err) {
      console.error('[SupplyTypeQuickCreate] error', err);
      if (err.response?.status === 409) {
        setError(`Le code "${codeClean}" existe déjà.`);
      } else {
        const d = err.response?.data;
        const detail = Array.isArray(d?.details) && d.details.length
          ? d.details.map(x => `${x.path} — ${x.message}`).join(' ; ')
          : null;
        setError(detail ? `Données invalides : ${detail}` : (d?.message || 'Erreur lors de la création'));
      }
      setSaving(false);
    }
  };

  return (
    <div onClick={onClose}
         style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  zIndex: 60 }}>
      <form className="modal-mobile-fullscreen" onClick={e => e.stopPropagation()} onSubmit={handleSubmit}
            style={{ background: 'white', borderRadius: 8, width: '100%', maxWidth: 440,
                     boxShadow: '0 10px 30px rgba(0,0,0,0.2)' }}>

        <div style={{ padding: '14px 20px', borderBottom: '1px solid #e2e8f0' }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 500 }}>Nouveau type de fourniture</h2>
        </div>

        <div style={{ padding: 20, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <label style={{ gridColumn: '1 / -1', display: 'flex', flexDirection: 'column' }}>
            <span style={labelTextStyle}>Code *</span>
            <input type="text" value={code}
                   onChange={e => setCode(e.target.value.toUpperCase())}
                   placeholder="ex: CAS22, FILMAGE_PAL"
                   required disabled={saving} style={inputStyle} />
            <span style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
              Majuscules, chiffres, underscore. Sera utilisé comme identifiant.
            </span>
          </label>

          <label style={{ gridColumn: '1 / -1', display: 'flex', flexDirection: 'column' }}>
            <span style={labelTextStyle}>Libellé *</span>
            <input type="text" value={label}
                   onChange={e => setLabel(e.target.value)}
                   placeholder="ex: Carton CAS22"
                   required disabled={saving} style={inputStyle} />
          </label>

          <label style={{ display: 'flex', flexDirection: 'column' }}>
            <span style={labelTextStyle}>Catégorie</span>
            <select value={category} onChange={e => setCategory(e.target.value)}
                    disabled={saving} style={inputStyle}>
              {CATEGORIES.map(c => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </label>

          <label style={{ display: 'flex', flexDirection: 'column' }}>
            <span style={labelTextStyle}>Unité</span>
            <select value={unit} onChange={e => setUnit(e.target.value)}
                    disabled={saving} style={inputStyle}>
              {UNITS.map(u => (
                <option key={u.value} value={u.value}>{u.label}</option>
              ))}
            </select>
          </label>

          <label style={{ gridColumn: '1 / -1', display: 'flex', flexDirection: 'column' }}>
            <span style={labelTextStyle}>Notes (optionnel)</span>
            <textarea value={notes} onChange={e => setNotes(e.target.value)}
                      rows={2} disabled={saving}
                      style={{ ...inputStyle, fontFamily: 'inherit', resize: 'vertical' }} />
          </label>

          {error && (
            <div style={{ gridColumn: '1 / -1', padding: 8, background: '#fee2e2',
                          color: '#991b1b', border: '1px solid #fca5a5',
                          borderRadius: 4, fontSize: 12 }}>{error}</div>
          )}
        </div>

        <div style={{ padding: '12px 20px', borderTop: '1px solid #e2e8f0',
                      display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" onClick={onClose} disabled={saving}
                  style={{ padding: '8px 16px', background: 'white',
                           border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13,
                           cursor: saving ? 'not-allowed' : 'pointer' }}>
            Annuler
          </button>
          <button type="submit" disabled={saving}
                  style={{ padding: '8px 16px', background: '#2563eb', color: 'white',
                           border: 'none', borderRadius: 4, fontSize: 13, fontWeight: 500,
                           cursor: saving ? 'not-allowed' : 'pointer',
                           opacity: saving ? 0.6 : 1 }}>
            {saving ? '…' : 'Créer'}
          </button>
        </div>
      </form>
    </div>
  );
}

const labelTextStyle = {
  fontSize: 12, color: '#475569', marginBottom: 4, fontWeight: 500,
};
const inputStyle = {
  width: '100%', padding: 8, border: '1px solid #d1d5db',
  borderRadius: 4, fontSize: 13, boxSizing: 'border-box',
};
